import { Link, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { Topbar } from '@/components/Topbar';
import { EmptyState } from '@/components/EmptyState';
import { useAsync } from '@/hooks/useAsync';
import { fmtRelative } from '@/lib/format';
import { buildTelegramChatUrl, buildTelegramMessageUrl, openTelegramUrl } from '@/lib/telegram-links';
import type { CampaignHistoryItem } from '@/types/domain';

export function CampaignHistoryPage(): JSX.Element {
  const { id } = useParams();
  const cid = Number(id);
  const { data, loading } = useAsync(() => api.getCampaignHistory(cid), [cid]);

  if (loading) {
    return (
      <div className="space-y-3">
        <Topbar title="История посевов" subtitle="реальные сообщения · reply-статистика" />
        <div className="skeleton h-24" />
        <div className="skeleton h-32" />
        <div className="skeleton h-32" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Topbar
        title="История посевов"
        subtitle={`${data?.summary.postings ?? 0} посевов · ${data?.summary.messages ?? 0} сообщений`}
      />

      <section className="grid grid-cols-2 gap-3">
        <MetricCard label="reply" value={data?.summary.replies ?? 0} />
        <MetricCard label="mentions" value={data?.summary.mentions ?? 0} />
        <MetricCard label="лс" value={data?.summary.dms ?? 0} />
        <MetricCard label="сообщений" value={data?.summary.messages ?? 0} />
      </section>

      {!loading && !(data?.items.length) && (
        <EmptyState
          icon="message"
          title="История пока пустая"
          body="Здесь появятся только реально отправленные посевы и ответы на них."
        />
      )}

      <div className="space-y-3">
        {(data?.items ?? []).map((item) => (
          <HistoryCard key={item.id} campaignId={cid} item={item} />
        ))}
      </div>
    </div>
  );
}

function HistoryCard({
  campaignId,
  item,
}: {
  campaignId: number;
  item: CampaignHistoryItem;
}): JSX.Element {
  return (
    <section className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/campaigns/${campaignId}/chats/${item.target.id}`}
            className="truncate text-sm font-semibold text-ink-50"
          >
            {item.target.title || item.target.normalizedLink}
          </Link>
          <div className="mt-1 text-2xs text-ink-500">
            {item.target.normalizedLink} · старт {fmtRelative(item.scheduledAt)}
          </div>
        </div>
        <span className={`rounded-full px-3 py-1 text-2xs font-semibold ${statusClass(item.status)}`}>
          {statusLabel(item.status)}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-2xs">
        <StatPill label="reply" value={item.stats.replyCount} />
        <StatPill label="mentions" value={item.stats.mentionCount} />
        <StatPill label="лс" value={item.stats.dmCount} />
        <StatPill label="сообщений" value={item.postedMessages.length} />
      </div>

      <div className="mt-3 space-y-2">
        {item.postedMessages.map((message) => (
          <button
            key={message.id}
            type="button"
            onClick={() =>
              openTelegramUrl(
                buildTelegramMessageUrl({
                  chatUrl: buildTelegramChatUrl(item.target.rawInput, item.target.normalizedLink),
                  tgChatId: message.tgChatId,
                  tgMessageId: message.tgMessageId,
                }),
              )
            }
            className="block w-full rounded-2xl border border-ink-700/60 bg-ink-900/70 px-3 py-2.5 text-left transition active:scale-[0.99]"
          >
            <div className="flex items-center gap-2 text-2xs text-ink-500">
              <span className="rounded-full bg-accent-500/15 px-2 py-0.5 font-semibold text-accent-200">
                {message.role || 'msg'}
              </span>
              <span>{message.accountName || `account ${message.accountId}`}</span>
              <span>·</span>
              <span>{fmtRelative(message.postedAt)}</span>
              {message.replyToTgId ? (
                <>
                  <span>·</span>
                  <span>reply</span>
                </>
              ) : null}
            </div>
            <div className="mt-2 text-sm leading-6 text-ink-100">{message.text}</div>
          </button>
        ))}
      </div>

      {item.lastError ? (
        <div className="mt-3 rounded-2xl border border-rose-400/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          {item.lastError}
        </div>
      ) : null}
    </section>
  );
}

function MetricCard({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-[24px] border border-white/8 bg-[#0f1530] px-4 py-4">
      <div className="text-xl font-semibold text-white">{value}</div>
      <div className="mt-1 text-2xs uppercase tracking-[0.18em] text-[#7d87ad]">{label}</div>
    </div>
  );
}

function StatPill({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <span className="rounded-full border border-ink-700/70 bg-ink-850 px-3 py-1 text-ink-300">
      {label}: {value}
    </span>
  );
}

function statusLabel(status: CampaignHistoryItem['status']) {
  switch (status) {
    case 'scheduled':
      return 'в очереди';
    case 'running':
      return 'идёт';
    case 'posted':
      return 'завершён';
    case 'failed':
      return 'ошибка';
    case 'cancelled':
      return 'отменён';
  }
}

function statusClass(status: CampaignHistoryItem['status']) {
  switch (status) {
    case 'posted':
      return 'bg-emerald-500/15 text-emerald-200';
    case 'failed':
      return 'bg-rose-500/15 text-rose-200';
    case 'running':
      return 'bg-sky-500/15 text-sky-200';
    default:
      return 'bg-ink-800 text-ink-200';
  }
}
